import React, { useState, useCallback, useRef, useEffect } from 'react';
import clsx from 'clsx';

import ExtendedTextField from '@src/stories/elements/ExtendedTextField/ExtendedTextField';
import ExtendedCheckbox from '@src/stories/elements/ExtendedCheckbox/ExtendedCheckbox';
import ExtendedTextareaField from '@src/stories/elements/ExtendedTextareaField/ExtendedTextareaField';
import ExtendedButton from '@src/stories/elements/ExtendedButton/ExtendedButton';
import ExtendedCustomSelectField from './ExtendedCustomSelectField';
import ExtendedCollapsible from "@src/stories/components/ExtendedCollapsible/ExtendedCollapsible";
import ExtendedCartMultiAddressInfo from './ExtendedCartMultiAddressInfo';
import { updateItems } from "frontend/js/useShopifyCart";
import { debounce } from 'alpinejs/src/utils/debounce';

const BUNDLE_TYPE = 'Bundle Container';


export default function ExtendedCartMultiItemDetails ({
  item,
  itemIndex,
  hasAddOns,
  cardTypes,
  giftWrapTypes,
  optionsInCart,
  updatingCart,
  isGiftWrapEnabled,
  hasOtherItems,
  translations,
  onItemDelete,
  onApplyMessageToAll,
  lineItem,
  isHamper,
  characterLimit,
  customer,
  onMultiAddressChange,
  multiAddress,
  messageUpdateTimestamp = Date.now() 
}) {

  const [isLoading, setIsLoading] = useState(false);
  const [hasCard, setHasCard] = useState(!!item.properties._card_type);
  const [emptyMessage, setEmptyMessage] = useState(item.properties._is_card_message_blank);
  const [isValid, setisValid] = useState(false);

  useEffect(() => {
    if (item.properties._card_message && !item.properties['Card Message']) {
      item.properties['Card Message'] = item.properties._card_message;
    } else if (item.properties['Card Message'] && !item.properties._card_message) {
      item.properties._card_message = item.properties['Card Message'];
    }
  }, []);

  async function handleQuantityUpdate(item, itemIndex) {
    if (isLoading) return;

    setIsLoading(true);
    const updates = {}

    updates[`${item.key}`] = 0;

    if (item.properties._card_type) {
      const optionInCart = optionsInCart[item.properties._card_type];
      if (optionInCart) {
        optionInCart.quantity -= 1

        if (optionInCart.key) {
          updates[`${optionInCart.key}`] = optionInCart.quantity;
        }
      }
    }

    if (item.properties._gift_wrap_type) {
      const optionInCart = optionsInCart[item.properties._gift_wrap_type];

      if (optionInCart) {
        optionInCart.quantity -= 1

        if (optionInCart.key) {
          updates[`${optionInCart.key}`] = optionInCart.quantity;
        }
      }
    }

    await updateItems({ updates });

    onItemDelete(item.variant_id, itemIndex);
    setIsLoading(false);
  }

  const handleChange = useCallback(
    debounce((event, item) => {
      const value = event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value

      item.properties[event.target.name] = value;
      
      if (event.target.name === '_card_message') {
        item.properties['Card Message'] = value;
      } else if (event.target.name === 'Card Message') {
        item.properties['_card_message'] = value;
      }
    }, 500), []
  );

  function handleCardTypeChange (value, item, name) {
    handleSelectChange(value, item, name);
    if(value == 0) {
      setHasCard(false);
    }
    else {
      setHasCard(!!value);
    }
  }

  function handleSelectChange (value, item, name) {
    // Wipe Card Message on Cart Type reset
    if(name == '_card_type' && value == 0) {
      item.properties['_card_message'] = '';
      item.properties['Card Message'] = ''; 
      // Clear any card-related errors when removing a card
      if (item.formErrors) {
        delete item.formErrors.card_message;
        delete item.formErrors.card_type;
      }
    }
    
    // Store the previous value before changing
    const previousValue = item.properties[name];
    
    if (item.properties[name] !== value) {
      if (value) {
        // Check if the card is available before associating
        if (name === '_card_type') {
          const cardType = cardTypes.find(card => card.id === value);
          if (!cardType || !cardType.available) {
            // Don't proceed with unavailable card
            console.warn("Card not available:", value);
            return;
          }
        }
        
        // Associate this item with this card type
        if (optionsInCart[value]) {
          // Only increment quantity if this is a new association
          if (!previousValue) {
            optionsInCart[value].quantity += 1;
          }
          optionsInCart[value].isUpdated = true;
          // Track which item this card belongs to
          optionsInCart[value].associatedItems = optionsInCart[value].associatedItems || [];
          if (!optionsInCart[value].associatedItems.includes(item.key)) {
            optionsInCart[value].associatedItems.push(item.key);
          }
        } else {
          optionsInCart[value] = {
            id: value,
            quantity: 1,
            isNew: true,
            associatedItems: [item.key]
          }
        }
      }
      
      // Handle removing previous card association
      if (previousValue && optionsInCart[previousValue]) {
        // Remove this item from the associated items
        if (optionsInCart[previousValue].associatedItems) {
          optionsInCart[previousValue].associatedItems = 
            optionsInCart[previousValue].associatedItems.filter(key => key !== item.key);
        }
        
        // Decrement quantity only if no more items are associated with this card
        if (!optionsInCart[previousValue].associatedItems?.length) {
          optionsInCart[previousValue].quantity = Math.max(0, optionsInCart[previousValue].quantity - 1);
          optionsInCart[previousValue].isUpdated = true;
        }
      }

      // Update the property on the item
      item.properties[name] = value;
      
      // Clear any card-related errors when changing card types
      if (name === '_card_type' && item.formErrors) {
        delete item.formErrors.card_message;
        delete item.formErrors.card_type;
      }
    }
  }

  const handleCheck = (event) => {
    console.log(event.target.checked);
    setEmptyMessage(event.target.checked);
  }

  const collapsibleRef = useRef(null);
  const [dropdown, setDropdown] = useState(true);

  const handleToggle = () => {
    if (collapsibleRef.current) {
      collapsibleRef.current.toggle();
    }
    setDropdown(!dropdown);
  };

  useEffect(() => {
    function isAddressValid(item, addresses){
      const address = addresses[item.key];
      const {properties: { _shipping_address1, _shipping_city , _shipping_country, _shipping_first_name, _shipping_last_name, _shipping_phone, _shipping_province,  _shipping_zip} } = item
      if (_shipping_address1 && _shipping_city && _shipping_country && _shipping_first_name && _shipping_last_name && _shipping_phone && _shipping_province && _shipping_zip ) return true;
      if (!address) return false;
  
      // Check required fields are non-empty
      const requiredFields = [
          "address[first_name]",
          "address[last_name]",
          "address[country]",
          "address[phone]",
          "address[address1]",
          "address[city]",
          "address[province]",
          "address[zip]"
      ];
  
      return requiredFields.every(field => address[field] && address[field] !== "");
  }
  
  setisValid(isAddressValid(item, multiAddress));
  
  }, [multiAddress]);

  return (
    <>
      <hr className={clsx('bg-mb-neutral-500 my-2',{
        'max-lg:hidden': itemIndex === 0
      })} />
      <div className={`p-4 extended-cart__multi-items border rounded-lg shadow-small flex flex-col gap-2   ${clsx({
            "border-mb-red extended-cart__multi-items-error": !isValid,
            "border-mb-green extended-cart__multi-items-valid": isValid,
            "border-mb-neutral-500": false
          })}`}>    
        
        <div className='flex items-center justify-between gap-4'>
          <div className="flex items-center gap-2">
            { isValid && <span className="icon icon-check-circle text-[18px] text-mb-green"> </span> }
            { !isValid && <span className="icon icon-x-circle text-[18px] text-mb-red"> </span> }
            <div className='extended-cart__multi-items-title'>
              {isHamper ? translations.hamper : translations.item } {itemIndex + 1}
            </div>
          </div>
          <div className="flex flex-row gap-[21px] items-center"> 
            <span
              className={clsx('text-2xl cursor-pointer', {
                'icon-trash': !isLoading,
                'icon-loader-alt animate-spin': isLoading
              })}
              onClick={() => handleQuantityUpdate(item, itemIndex)}
            ></span>

            <span className={`cursor-pointer text-[24px] icon ${ dropdown ? 'icon-chevron-up' : 'icon-chevron-down' }`} onClick={handleToggle}> </span>  
          </div>
        </div>
                
        <ExtendedCollapsible
          key={`${itemIndex}-collapsible`}
          active
          ref={collapsibleRef}
        >
          <form
            className='flex flex-col gap-4'
            onSubmit={(event) => event.preventDefault()}
            onChange={(event) => handleChange(event, item)}
          > 
          {hasAddOns &&
            <div className="mb-5">
              <div className='flex max-lg:flex-col gap-4 lg:gap-6'>
              { isHamper && <ExtendedCustomSelectField
                  name='_card_type'
                  placeholder='Select a card (optional)'
                  label={`Card ${itemIndex + 1}`}
                  options={cardTypes.filter(card => card.available)}
                  disabled={isLoading || updatingCart}
                  key={`card_type_${item.properties._card_type}`}
                  value={item.properties._card_type}
                  hasError={item.formErrors?.card_type}
                  errorMessage={item.formErrors?.card_type}
                  className='flex-1'
                  lineItem={lineItem}
                  onChange={(value) => handleCardTypeChange(value, item, '_card_type')}
                />
              }
                {isGiftWrapEnabled && isHamper &&
                  <ExtendedCustomSelectField
                    name='_gift_wrap_type'
                    label={`Gift wrapping ${itemIndex + 1}`}
                    options={giftWrapTypes}
                    lineItem={lineItem}
                    placeholder='Select a gift wrap (optional)'
                    disabled={isLoading || updatingCart}
                    key={`gift_wrap_type_${item.properties._gift_wrap_type}`}
                    value={item.properties._gift_wrap_type}
                    hasError={item.formErrors?.gift_wrap_type}
                    errorMessage={item.formErrors?.gift_wrap_type}
                    className='flex-1'
                    onChange={(value) => handleSelectChange(value, item, '_gift_wrap_type')}
                  />
                }
              </div>
              
              {hasCard &&
                <div className="mt-5">
                  <div className='flex items-center justify-between gap-4 mb-2'>
                    <div className='text-sm font-medium'>
                      {translations?.type_your_message || 'Type your message'}
                    </div>

                    <ExtendedCheckbox
                      className='item-details__blank-checkbox desktop'
                      key={`is_card_message_blank_${item.properties._is_card_message_blank}`}
                      checked={emptyMessage}
                      onChange={(event)=> { handleCheck(event) }}
                      disabled={isLoading || updatingCart}
                      name='_is_card_message_blank'
                      label={translations?.blank_message || 'I would like it to be left blank'}
                    />
                  </div>
                  <div className="extended-cart_message-field"> 
                    <ExtendedTextareaField
                      name='Card Message'
                      key={`card_message_${item.key}_${messageUpdateTimestamp}`}
                      disabled={ emptyMessage || isLoading || updatingCart}
                      value={emptyMessage ? '' : item.properties['Card Message'] || item.properties._card_message || '' }
                      required={!item.properties._is_card_message_blank}
                      maxCharacters={characterLimit}
                      hasError={item.formErrors?.card_message}
                      errorMessage={item.formErrors?.card_message}
                      disableEmoji
                      showCharactersLeft={false}
                    />
                  </div>

                  <ExtendedCheckbox
                    className='item-details__blank-checkbox mobile'
                    key={`is_card_message_blank_${item.properties._is_card_message_blank}`}
                    checked={emptyMessage}
                    onChange={(event)=> { handleCheck(event) }}
                    disabled={ emptyMessage || isLoading || updatingCart}
                    name='_is_card_message_blank'
                    label={translations?.blank_message || 'I would like it to be left blank'}
                  />

                  {hasOtherItems &&
                    <ExtendedButton
                      type='button'
                      label={translations?.all_message || 'Apply message to all'}
                      style='secondary'
                      className='mt-4'
                      disabled={isLoading || updatingCart}
                      onClick={() => onApplyMessageToAll(item.properties._card_message)}
                    />
                  }
                </div>
              }
      
            </div>
          }
            
    
          </form>
          <ExtendedCartMultiAddressInfo
                item={item}
                customer={customer}
                multiAddress={multiAddress}
                translations={translations}
                onAddressChange={(formObject) => onMultiAddressChange(formObject)}

          /> 
      </ExtendedCollapsible>

      </div>
    </>
  )
}